import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { EditOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import axios, { AxiosError } from "axios";
import { Input, message, Spin } from "antd";

import { getUserDetails, postUserDetails } from "../axios/portalData";
import UserContext from "../themeContext/UserContext";

import "./account.less";

type Status = "error" | "warning" | "";

const Account: React.FC = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const { setUserInfo } = useContext(UserContext);

  // get userDetails api
  useEffect(() => {
    getUserDetails()
      .then((res) => {
        setName(res.name);
        setEmail(res.email);
        setIsLoading(false);
      })
      .catch((error: Error | AxiosError) => {
        if (axios.isAxiosError(error)) {
          console.log("AxiosError message: ", error);
          message.error(error.message);
        } else {
          console.log("unexpected error: ", error);
          message.error("An unexpected error occurred");
        }
      });
  }, []);

  const handleClickDone = (object: { [key: string]: string }, setState: Dispatch<SetStateAction<string>>) => {
    setIsLoading(true);
    let value = "";
    if (object["name"]) {
      value = object["name"];
      setUserInfo((userInfo) => {
        return { ...userInfo, firstName: value };
      });
    }
    if (object["email"]) {
      value = object["email"];
    }
    if (object["password"]) {
      value = object["password"];
    }
    postUserDetails(object)
      .then((res) => {
        res.status == 200 ? message.success(res.data) : message.error(res.data);
        setState(value);
        setIsLoading(false);
      })
      .catch((error: Error | AxiosError) => {
        if (axios.isAxiosError(error)) {
          console.log("AxiosError message: ", error);
          message.error(error.message);
          setIsLoading(false);
        } else {
          console.log("unexpected error: ", error);
          message.error("An unexpected error occurred");
          setIsLoading(false);
        }
      });
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }
  return (
    <div style={{ margin: "108px 383px 100px 383px" }}>
      <RenderOptions _key="Name" value={name} handleClickDone={(e) => handleClickDone({ name: e }, setName)} />
      <RenderOptions _key="Email" value={email} handleClickDone={(e) => handleClickDone({ email: e }, setEmail)} />
      <RenderOptions
        _key="Password"
        value={password}
        handleClickDone={(e) => handleClickDone({ password: e }, setPassword)}
        isPass={true}
      />
    </div>
  );
};

export default Account;

interface RenderOptionsProps extends TextEditCellProps {
  _key: string;
}
const RenderOptions: React.FC<RenderOptionsProps> = (props) => {
  const { _key, value, handleClickDone, isPass } = props;

  return (
    <>
      <div style={{ margin: "50px 0px 16px 0px", height: "48px" }} className="align-items-center flex space-between">
        <div className="account-key">{_key}</div>
        <TextEditCell value={value} handleClickDone={handleClickDone} isPass={isPass} />
      </div>
      <hr />
    </>
  );
};

interface TextEditCellProps {
  value: string;
  handleClickDone: (value: string) => void;
  isPass?: boolean;
}
export const TextEditCell: React.FC<TextEditCellProps> = (props) => {
  const { value, handleClickDone, isPass } = props;
  const [isEdit, setIsEdit] = useState(false);
  const [typedValue, setTypedValue] = useState(value);
  const [status, setStatus] = useState<Status>("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (isPass) {
      const charClasses =
        Number(/[A-Z]+/g.test(typedValue)) + //UpperCase Check
        Number(/[a-z]+/g.test(typedValue)) + //LowerCase Check
        Number(/\d+/g.test(typedValue)) + //Number Check
        Number(/[^a-zA-Z0-9]+/g.test(typedValue)); // Symbol Check

      if (typedValue.length >= 8 && charClasses >= 3) {
        setStatus("");
      } else if (typedValue.length < 8) {
        setStatus("error");
        setErrorMessage("min of 8 chars");
      } else {
        setStatus("error");
        setErrorMessage("Needs 3 of 4 - upper, lower, number, symbol");
      }
    } else {
      if (typedValue.length == 0) {
        setStatus("error");
        setErrorMessage("Cannot be blank");
      } else {
        setStatus("");
      }
    }
  }, [typedValue]);

  const closeHandleClick = () => {
    setIsEdit(false);
    setTypedValue(value);
  };

  const checkedHandleClick = (typedValue: string) => {
    handleClickDone(typedValue);
    setIsEdit(false);
  };

  return (
    <div className="flex align-items-center" style={{ justifyContent: "space-between" }}>
      {isEdit ? (
        <>
          <div>
            <Input
              status={status}
              className="selected-icon-input"
              placeholder={value}
              onChange={(e) => setTypedValue(e.target.value)}
              value={typedValue}
              onPressEnter={() => checkedHandleClick(typedValue)}
            />
            {status == "error" ? <div style={{ color: "var(--red-1)", fontSize: "12px" }}>{errorMessage}</div> : <></>}
          </div>
          <div style={{ display: "flex", flexDirection: "row", marginLeft: 20, gap: 5 }}>
            <button
              disabled={status == "error" ? true : false}
              style={{ fontSize: "25px" }}
              className="icon-button"
              onClick={() => checkedHandleClick(typedValue)}
            >
              {<CheckOutlined />}
            </button>
            <button style={{ fontSize: "25px" }} className="icon-button" onClick={closeHandleClick}>
              {<CloseOutlined />}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="account-value">{isPass ? "***********" : value}</div>
          <button
            style={{ fontSize: "25px", marginLeft: "30px" }}
            className="icon-button"
            onClick={() => setIsEdit(!isEdit)}
          >
            {<EditOutlined />}
          </button>
        </>
      )}
    </div>
  );
};
